/**
 * VinculosCard — vista inversa de la cadena de propiedad.
 *
 * En el expediente de una persona (natural o jurídica), lista las entidades para
 * las cuales ESTE sujeto está registrado como beneficiario final
 * (`GET /api/v1/expedientes/{id}/vinculos`). Así el expediente de un UBO
 * persona-natural muestra sus entidades "madre" SIN reintroducir las pestañas
 * Sujetos/Beneficiarios (que son solo de organizaciones). Se oculta por completo
 * cuando no hay vínculos. Cadenas es-PA únicamente.
 */
import { useNavigate } from 'react-router-dom';
import { useVinculos } from '../lib/hooks';
import { Card, Pill } from './ui';
import { PersonaIcon } from './icons';
import { ChevronRight, Link2 } from 'lucide-react';

function formatPct(pct: number): string {
  if (!Number.isFinite(pct)) return '—';
  return `${Math.round(pct * 100) / 100}%`;
}

export default function VinculosCard({ expedienteId }: { expedienteId: string }) {
  const navigate = useNavigate();
  const { data } = useVinculos(expedienteId);

  if (!data || data.length === 0) return null;

  return (
    <Card className="mt-6">
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-primary/10">
          <Link2 className="h-4 w-4 text-primary-dark" strokeWidth={2} />
        </span>
        <h2 className="font-serif text-lg font-medium text-ink">Vínculos</h2>
      </div>
      <p className="mt-1 text-sm text-ink-soft">Esta persona es beneficiario final de:</p>

      <div className="mt-3 grid gap-2">
        {data.map((v) => (
          <button
            key={v.ownerId}
            onClick={() => navigate(`/expedientes/${v.expedienteId}`)}
            className="flex items-center gap-3 rounded-xl border border-line bg-panel px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-canvas [&>*]:min-w-0"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-canvas">
              <PersonaIcon persona="legal" className="h-5 w-5 text-primary-dark" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-bold text-ink">{v.entityName}</span>
              <span className="block truncate font-mono text-[10.5px] text-ink-faint">
                {v.entityKind || 'Persona jurídica'}
              </span>
            </span>
            <Pill tone="blue" className="shrink-0">
              {formatPct(v.ownershipPct)}
            </Pill>
            <ChevronRight className="h-4 w-4 shrink-0 text-ink-faint" strokeWidth={2} />
          </button>
        ))}
      </div>
    </Card>
  );
}
